import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { authService } from '@/services/auth.service'

export interface User {
  id: string
  name: string
  email: string
  avatar?: string
  roles?: string[]
  permissions?: string[]
}

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<User | null>(null)
  const token = ref<string | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  // Getters
  const isAuthenticated = computed(() => !!token.value && !!user.value)
  const userName = computed(() => user.value?.name || '')
  const userEmail = computed(() => user.value?.email || '')

  // Actions
  function login(userData: User, authToken: string) {
    user.value = userData
    token.value = authToken
    error.value = null
    // Сохраняем в localStorage для персистентности
    localStorage.setItem('auth_token', authToken)
    localStorage.setItem('user_data', JSON.stringify(userData))
  }

  async function logout() {
    isLoading.value = true
    try {
      // Вызываем API для выхода
      await authService.logout()
    } catch (err) {
      console.error('Logout API error:', err)
      // Продолжаем очистку даже при ошибке API
    } finally {
      // Очищаем состояние
      user.value = null
      token.value = null
      error.value = null
      isLoading.value = false
      // Очищаем localStorage
      localStorage.removeItem('auth_token')
      localStorage.removeItem('user_data')
    }
  }

  async function restoreSession() {
    const savedToken = localStorage.getItem('auth_token')
    const savedUser = localStorage.getItem('user_data')
    
    if (savedToken && savedUser) {
      try {
        token.value = savedToken
        user.value = JSON.parse(savedUser)
        
        // Проверяем действительность токена
        const isValid = await authService.verifyToken()
        if (!isValid) {
          throw new Error('Invalid token')
        }
      } catch (error) {
        console.error('Error restoring session:', error)
        await logout()
      }
    }
  }

  async function loginWithGoogle() {
    isLoading.value = true
    error.value = null
    try {
      await authService.loginWithGoogle()
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Ошибка авторизации через Google'
      isLoading.value = false
    }
  }

  async function loginWithYandex() {
    isLoading.value = true
    error.value = null
    try {
      await authService.loginWithYandex()
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Ошибка авторизации через Yandex'
      isLoading.value = false
    }
  }

  function clearError() {
    error.value = null
  }

  // Для демонстрации - имитация авторизованного пользователя
  function loginDemo() {
    const demoUser: User = {
      id: '1',
      name: 'John Doe',
      email: 'john.doe@example.com',
      avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80'
    }
    login(demoUser, 'demo_token_12345')
  }

  return {
    // State
    user,
    token,
    isLoading,
    error,
    // Getters
    isAuthenticated,
    userName,
    userEmail,
    // Actions
    login,
    logout,
    restoreSession,
    loginWithGoogle,
    loginWithYandex,
    clearError,
    loginDemo
  }
}) 